import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Sun, Zap, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';
const LOGO_TRUE_URL = 'https://customer-assets.emergentagent.com/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/o7rjkvco_Updated%20Logo.png';

export const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_bfd95031-64a6-456f-91f3-7f07afaee79d/artifacts/ocmwn2qb_evans%20orchard.avif')`,
        }}
      >
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="mb-6">
            <img 
              src={LOGO_URL} 
              alt="Helios Electrical and Solar" 
              className="h-16 w-auto"
            />
          </div>
          <p className="text-xl text-slate-100 max-w-md leading-relaxed drop-shadow">
            Empowering sustainable energy transitions with precision and reliability.
          </p>
          <div className="flex gap-6 mt-8">
            <div className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-sky-300" />
              <span className="text-sm text-slate-100">500+ Installations</span>
            </div>
            <div className="flex items-center gap-2">
              <Sun className="w-5 h-5 text-sky-300" />
              <span className="text-sm text-slate-100">10MW+ Deployed</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-slate-50 dark:bg-slate-950 relative">
        {/* Top-right customer-portal redirect banner — makes it obvious this is
            the STAFF login, not the customer portal. */}
        <div
          className="absolute top-4 right-4 sm:top-6 sm:right-6 flex items-center gap-2 bg-white border border-slate-200 shadow-sm rounded-full px-3 py-1.5 text-xs"
          data-testid="customer-portal-banner"
        >
          <span className="text-slate-500 hidden sm:inline">Customer?</span>
          <Link
            to="/portal/login"
            className="text-blue-600 hover:text-blue-700 font-semibold inline-flex items-center gap-1"
            data-testid="customer-portal-link-top"
          >
            Customer Portal
            <span aria-hidden>→</span>
          </Link>
        </div>

        <div className="w-full max-w-md">
          {/* Helios logo above the card — visible on both desktop & mobile so the
              brand is anchored on the right side of the screen as well. */}
          <div className="flex items-center justify-center mb-6">
            <img
              src={LOGO_TRUE_URL}
              alt="Helios Electrical and Solar"
              className="h-16 w-auto drop-shadow-sm"
              data-testid="staff-login-right-logo"
            />
          </div>

          {/* Prominent "Staff Sign-In" pill so users can't mistake this for the
              customer portal. */}
          <div className="flex justify-center mb-4">
            <span
              className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-800 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide"
              data-testid="staff-login-badge"
            >
              <Zap className="w-3.5 h-3.5" />
              Staff Sign-In
            </span>
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle
                className="text-2xl font-bold font-['Manrope'] text-slate-900"
                data-testid="staff-login-title"
              >
                Helios CRM — Staff Login
              </CardTitle>
              <CardDescription className="text-slate-500">
                Internal sign-in for Helios team members. If you're a customer, please use the{' '}
                <Link
                  to="/portal/login"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="customer-portal-link-inline"
                >
                  Customer Portal
                </Link>{' '}
                instead.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="bg-white"
                    data-testid="login-email"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-700">Password</Label>
                    <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                      className="bg-white pr-10"
                      data-testid="login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign in'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-3">
                <p className="text-sm text-slate-500">
                  Don't have an account?{' '}
                  <Link
                    to="/register"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                    data-testid="register-link"
                  >
                    Create one
                  </Link>
                </p>
                <p className="text-xs text-slate-400">
                  Are you a customer?{' '}
                  <Link
                    to="/portal/login"
                    className="text-blue-500 hover:text-blue-600"
                    data-testid="customer-portal-link"
                  >
                    Access Customer Portal →
                  </Link>
                </p>
                <p className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                  <Link
                    to="/privacy-policy"
                    className="hover:text-slate-600"
                    data-testid="privacy-policy-link"
                  >
                    Privacy Policy
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
